/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';

import FilterContainer from 'containers/FilterContainer';
import { ListingWrapper } from 'views/Category';
import MaxWidthWrapper from 'components/wrappers/MaxWidthWrapper';
import NoResultComponent from 'components/NoResultComponent';
import ProductListing from 'components/products/ProductListing';
import PropTypes from 'prop-types';
import SearchInput from 'components/pages/search/SearchInput';
import { above } from 'utils/mediaqueries';
import colors from 'config/theme/colors';
import { extractQueryParams } from 'utils/query';
import getFilterConfig from 'config/filters';
import getSortConfig from 'config/sorts';
import { headingObjectProp } from 'utils/proptypes/modules/textProps';
import sanitizeUserInput from 'utils/sanitizeUserInput';
import styled from 'libs/styled';
import useCalculatePagesize from 'hooks/useCalculatePagesize';
import { useHistory } from 'react-router-dom';
import useSaveResponse from 'hooks/useSaveResponse';
import { useTranslation } from 'react-i18next';
import zIndex from 'config/theme/z-index';

const Heading = styled('h1')`
    margin-top: 12px;
    font-size: 1.4rem;
    line-height: 1;

    ${above.lg} {
        font-size: 1.6rem;
        margin-top: 16px;
    }
`;

// Heading 1 styling
const InputWrapper = styled('div')`
    display: flex;
    width: 100%;
    padding: 16px 0;
    font-size: 3.2rem;
    letter-spacing: -0.03em;
    line-height: 1;

    ${above.sm} {
        padding: 24px 0;
        font-size: 5.6rem;
    }

    ${above.xl} {
        font-size: 8.8rem;
    }
`;

const Search = ({ data }) => {
    // Setup
    const { t } = useTranslation();
    const pageSize = useCalculatePagesize();
    const history = useHistory();
    const maxSearch = 100; //characters
    const delay = 500; //ms

    const [state, setState] = useState({
        searchString: decodeURIComponent(sanitizeUserInput(history.location.search.replace('?s=', ''), maxSearch)),
        numberOfProducts: 0,
    });

    const { heading, no_search_result: noSearchResult, no_search: noSearch, placeholder_text: placeholderText } =
        data || {};

    // Save response from container
    const [savedResponse, saveResponse] = useSaveResponse();

    // Text
    const title = heading || t('search.search_results');
    const noResultText = noSearchResult?.text || null;
    const noSearchText = noSearch?.text || null;

    // Listen to changes in the url via history
    // This will trigger after handleInputChange
    useEffect(() => {
        const unregisterHistoryListener = history.listen(location => {
            const queryParams = extractQueryParams(location.search);
            const newSearchstring = sanitizeUserInput(queryParams.s, maxSearch);
            if (newSearchstring !== state.searchString) {
                setState(state => ({
                    ...state,
                    searchString: newSearchstring,
                }));
            }
        });
        return () => {
            unregisterHistoryListener();
        };
    }, [state.searchString]);

    // Update search value in url
    // This will trigger getWindowSearchString
    const handleInputChange = dirtyString => {
        const string = sanitizeUserInput(dirtyString, maxSearch);
        history.push({
            search: `?s=${encodeURI(string)}`,
        });
    };

    const filterConfig = getFilterConfig(t);
    const sortConfig = getSortConfig(t);

    return (
        <>
            <MaxWidthWrapper
                includeContentMargins
                position="relative"
                marginBottom="-1px" // Hides filter border until stuck
                borderBottom={`1px solid ${colors.black}`}
                zIndex={zIndex.filterBar + 1}
            >
                <InputWrapper>
                    <SearchInput
                        delay={delay}
                        maxSearch={maxSearch}
                        placeholderText={placeholderText}
                        searchString={state.searchString}
                        updateSearch={handleInputChange}
                    />
                </InputWrapper>
                <Heading>{title}</Heading>
            </MaxWidthWrapper>

            <ListingWrapper borderTop={`1px solid ${colors.black}`}>
                {state.searchString ? (
                    <FilterContainer
                        pageSize={pageSize}
                        search={state.searchString}
                        config={filterConfig}
                        sorts={sortConfig}
                        render={({ response }) => {
                            saveResponse(response);
                            setState(prevState => ({
                                ...prevState,
                                numberOfProducts: savedResponse.current?.hitsCount || 0,
                            }));

                            return <ProductListing response={savedResponse.current} noResultText={noResultText} />;
                        }}
                    />
                ) : (
                    <MaxWidthWrapper includeContentMargins>
                        <NoResultComponent text={noSearchText} loading={false} />
                    </MaxWidthWrapper>
                )}
            </ListingWrapper>
        </>
    );
};

Search.propTypes = {
    data: PropTypes.shape({
        'no_search_result': { text: PropTypes.string },
        heading: {
            heading: PropTypes.shape({ headingObjectProp }),
        },
    }).isRequired,
};

export default Search;
